import axiosInstance from './axiosInstance'

// ── Enums & Constants ─────────────────────────────────────────────────────────

export const LEAD_SOURCES = [
  'FACEBOOK', 'INSTAGRAM', 'WHATSAPP', 'WEBSITE', 'REFERRAL', 'WALK_IN', 'PHONE_CALL', 'OTHER',
] as const
export type LeadSource = typeof LEAD_SOURCES[number]

export const INDUSTRY_TYPES = [
  'Technology', 'Healthcare', 'Finance & Banking', 'Real Estate',
  'Education', 'Retail & E-Commerce', 'Manufacturing', 'Automobile',
  'Hospitality & Travel', 'Media & Entertainment', 'Logistics & Supply Chain',
  'Construction', 'Agriculture', 'Pharmaceuticals', 'Telecommunications',
  'Legal & Consulting', 'Non-Profit / NGO', 'Government', 'Other',
] as const
export type IndustryType = typeof INDUSTRY_TYPES[number]

export const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'JUNK'] as const
export type LeadStatus = typeof LEAD_STATUSES[number]

export const LEAD_ACTIVITY_TYPES = [
  'LEAD_CREATED', 'LEAD_UPDATED', 'STATUS_CHANGE', 'CALL', 'NOTE', 'FOLLOW_UP', 'ASSIGNMENT', 'TAG_UPDATE', 'EMAIL', 'LEAD_CONVERTED', 'TASK',
] as const
export type LeadActivityType = typeof LEAD_ACTIVITY_TYPES[number]

export const MANUAL_ACTIVITY_TYPES = ['CALL', 'NOTE', 'FOLLOW_UP', 'TASK'] as const
export type ManualActivityType = typeof MANUAL_ACTIVITY_TYPES[number]

export const SOURCE_LABELS: Record<string, string> = {
  FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram', WHATSAPP: 'WhatsApp',
  WEBSITE: 'Website', REFERRAL: 'Referral',
  WALK_IN: 'Walk-in', PHONE_CALL: 'Phone Call', OTHER: 'Other',
}

export const STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: 'New', CONTACTED: 'Contacted', QUALIFIED: 'Qualified', CONVERTED: 'Converted', JUNK: 'Junk',
}

/** Returns the next status in the lifecycle, or null if terminal */
export function nextStatus(current: LeadStatus): LeadStatus | null {
  const idx = LEAD_STATUSES.indexOf(current)
  if (idx === -1 || idx >= LEAD_STATUSES.length - 2) return null // CONVERTED or QUALIFIED → convert
  return LEAD_STATUSES[idx + 1]
}

// ── Shared types ──────────────────────────────────────────────────────────────

export interface AuditUser {
  id: string
  name: string
  email: string
  avatarUrl: string | null
}

export interface TagMini {
  id: string
  name: string
  color: string
}

// ── Lead types ────────────────────────────────────────────────────────────────

export interface Lead {
  id:                    string
  name:                  string
  phone:                 string
  email:                 string | null
  source:                string
  status:                LeadStatus
  assignedAt:            string | null
  convertedAt:           string | null
  convertedToCustomerId: string | null
  convertedToDealId?:     string | null
  bestTimeToContact:     string | null
  nextAction:            string | null
  owner:                 AuditUser
  assignedTo:            AuditUser | null
  createdBy:             AuditUser | null
  tags:                  TagMini[]
  count:                { activities: number }
  createdAt:             string
  updatedAt:             string
  // Tenant context
  orgId:                 string | null
  orgName:               string | null
  lobId:                 string | null
  lobName:               string | null
  warehouseId:           string | null
  warehouseName:         string | null
  companyName:           string | null
  address:               string | null
  gst:                   string | null
  pincode:               string | null
  designation:           string | null
  websiteUrl:            string | null
  industryType:          string | null
  customerTypeId:        number | null
  customerTypeName:      string | null
  fileType:              string | null
  requirements?:          string | null
  expectedCloseDate?:     string | null
  expectedAmount?:        number | null
  helpAssigneeId?:       string | null
  helpAssigneeName?:     string | null
  helpAssignee?:         AuditUser | null
}

export interface PaginatedLeads {
  items:      Lead[]
  total:      number
  page:       number
  limit:      number
  totalPages: number
}

export interface LeadFilters {
  search?:          string
  statuses?:        (LeadStatus | 'ALL')[]
  assignedToIds?:   string[]
  ownerIds?:        string[]
  sources?:         string[]
  tagIds?:          string[]
  companyName?:     string
  // SuperAdmin cross-org drill-down
  orgIds?:          string[]
  lobIds?:          string[]
  warehouseIds?:    string[]
  fromDate?:        string
  toDate?:          string
  customerTypeIds?: number[]
  page?:            number
  pageSize?:        number
}

export interface CreateLeadPayload {
  name:         string
  phone:        string
  email?:       string
  source?:      LeadSource
  assignedToId?: string
  // SuperAdmin can specify tenant on creation
  orgId?:        string
  lobId?:        string
  warehouseId?:  string
  companyName?:  string
  address?:      string
  gst?:          string
  pincode?:      string
  designation?:    string
  websiteUrl?:     string
  industryType?:   string
  customerTypeId?: number | null
  nextAction?:     string | null
  fileType?:       string | null
  requirements?:      string | null
  bestTimeToContact?: string | null
  expectedCloseDate?: string | null
  expectedAmount?:    number | null
}

export interface UpdateLeadPayload {
  name?:              string
  phone?:             string
  email?:             string | null
  source?:            LeadSource
  bestTimeToContact?: string | null
  nextAction?:        string | null
  companyName?:       string | null
  orgId?:             string | null
  lobId?:             string | null
  warehouseId?:       string | null
  address?:           string | null
  gst?:               string | null
  pincode?:           string | null
  designation?:       string | null
  websiteUrl?:        string | null
  industryType?:      string | null
  customerTypeId?:    number | null
  fileType?:           string | null
  requirements?:       string | null
  expectedCloseDate?:  string | null
  expectedAmount?:     number | null
}

export interface ConvertLeadPayload {
  companyName?: string
  city?:        string
  dealName?:    string
  dealStage?:   string
  closingDate?: string
  amount?:      number
  description?: string
  source?:      string
  priority?:    string
  requirements?: string
  customerTypeId?: number | null
}

export type BulkUploadJobStatus = 'Queued' | 'Processing' | 'Completed' | 'Failed' | 'CancelRequested' | 'Cancelled'

export interface BulkUploadJobDto {
  id: string
  fileName: string
  status: BulkUploadJobStatus
  totalRows: number
  processedRows: number
  succeededCount: number
  skippedCount: number
  failedCount: number
  retryCount: number
  errorMessage: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
}

export interface BulkUploadJobLogDto {
  id: string
  rowNumber: number
  status: string
  message: string
  createdAt: string
}

export interface PaginatedBulkUploadJobLogs {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  items: BulkUploadJobLogDto[]
}

// ── Activity types ────────────────────────────────────────────────────────────

export interface LeadActivity {
  id:           string
  leadId:       string
  type:         LeadActivityType
  description:  string
  metadata:     Record<string, unknown> | null
  performedBy:  AuditUser
  createdAt:    string
}

export interface AddActivityPayload {
  type:          ManualActivityType
  description:   string
  performedById: string
  metadataJson?: string
}

// ── Status history types ──────────────────────────────────────────────────────

export interface StatusHistoryEntry {
  id:         string
  leadId:     string
  fromStatus: LeadStatus | null
  toStatus:   LeadStatus
  changedAt:  string
  changedBy:  AuditUser
}

// ── Tag types ─────────────────────────────────────────────────────────────────

export interface Tag {
  id:        string
  name:      string
  color:     string
  createdBy: AuditUser | null
  createdAt: string
  updatedAt: string
  count:    { leads: number }
  orgId?:    string | null
  orgName?:  string | null
}

export interface CreateTagPayload {
  name:   string
  color?: string
  /** SuperAdmin only: the org this tag should belong to. */
  orgId?: string
}

export interface UpdateTagPayload {
  name?:  string
  color?: string
}

// ═════════════════════════════════════════════════════════════════════════════
// API functions
// ═════════════════════════════════════════════════════════════════════════════

export interface LeadStatusSummary {
  status: string
  count: number
}

export interface LeadsSummary {
  totalLeads: number
  statusSummaries: LeadStatusSummary[]
}

export const leadsApi = {

  getSummary: async (filters: LeadFilters = {}): Promise<LeadsSummary> => {
    const res = await axiosInstance.get<{ data: LeadsSummary }>('/leads/summary', {
      params: filters,
      paramsSerializer: { indexes: null },
    })
    return res.data.data
  },

  // ── Lead CRUD ───────────────────────────────────────────────────────────────

  export: async (filters: LeadFilters = {}): Promise<void> => {
    const res = await axiosInstance.get('/leads/export', { params: filters, responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Leads.xlsx')
    document.body.appendChild(link)
    link.click()
    link.remove()
  },

  getAll: async (filters: LeadFilters = {}): Promise<PaginatedLeads> => {
    const res = await axiosInstance.get<{ data: any }>('/leads', { params: filters })
    const raw = res.data.data
    // Normalise both response shapes:
    //   ApiResults.Paged  → { items, total, page, limit, totalPages }
    //   ApiResults.Ok     → { results, rowCount, currentPage, pageSize }
    return {
      items:      raw.items      ?? raw.results     ?? [],
      total:      raw.total      ?? raw.rowCount     ?? 0,
      page:       raw.page       ?? raw.currentPage  ?? 1,
      limit:      raw.limit      ?? raw.pageSize     ?? 20,
      totalPages: raw.totalPages ?? (raw.pageSize > 0
        ? Math.ceil((raw.rowCount ?? 0) / raw.pageSize)
        : 1),
    }
  },

  /** All distinct, non-empty source values in the Leads table (tenant-scoped), sorted A→Z. */
  getSources: async (): Promise<string[]> => {
    const res = await axiosInstance.get<{ data: string[] }>('/leads/sources')
    return res.data.data ?? []
  },

  getById: async (id: string): Promise<Lead> => {
    const res = await axiosInstance.get<{ data: Lead }>(`/leads/${id}`)
    return res.data.data
  },

  create: async (payload: CreateLeadPayload): Promise<Lead> => {
    const res = await axiosInstance.post<{ data: Lead }>('/leads', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateLeadPayload): Promise<Lead> => {
    const res = await axiosInstance.put<{ data: Lead }>(`/leads/${id}`, payload)
    return res.data.data
  },

  updateRequirements: async (id: string, requirements: string | null): Promise<Lead> => {
    const res = await axiosInstance.patch<{ data: Lead }>(`/leads/${id}/requirements`, { requirements })
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/leads/${id}`)
  },

  bulkDelete: async (ids: string[]): Promise<void> => {
    await axiosInstance.delete('/leads/bulk', { data: { leadIds: ids } })
  },

  bulkAssign: async (ids: string[], assignedToId: string | null): Promise<void> => {
    await axiosInstance.patch('/leads/bulk/assign', { leadIds: ids, assignedToId: assignedToId || null })
  },

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  changeStatus: async (id: string, status: LeadStatus): Promise<Lead> => {
    const res = await axiosInstance.patch<{ data: Lead }>(`/leads/${id}/status`, { status })
    return res.data.data
  },

  assign: async (id: string, assignedToId: string | null): Promise<Lead> => {
    const res = await axiosInstance.patch<{ data: Lead }>(`/leads/${id}/assign`, {
      assignedToId: assignedToId || null,
    })
    return res.data.data
  },

  convert: async (id: string, payload: ConvertLeadPayload = {}): Promise<any> => {
    const res = await axiosInstance.post<{ data: any; Data?: any }>(`/leads/${id}/convert`, payload)
    return res.data.data ?? res.data.Data
  },

  bulkUpload: async (
    file: File,
    mappings: Record<string, string>,
    options?: {
      orgId?:             string
      lobId?:             string
      onUploadProgress?:  (percent: number) => void
      signal?:            AbortSignal
    }
  ): Promise<{ jobId: string; totalRows: number }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mappings', JSON.stringify(mappings))
    if (options?.orgId) formData.append('orgId', options.orgId)
    if (options?.lobId) formData.append('lobId', options.lobId)
    const res = await axiosInstance.post<{ jobId: string; totalRows: number }>('/leads/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: options?.signal,
      onUploadProgress: (e) => {
        if (options?.onUploadProgress && e.total) {
          options.onUploadProgress(Math.round((e.loaded / e.total) * 100))
        }
      },
    })
    return res.data
  },

  getBulkUploadStatus: async (jobId: string): Promise<BulkUploadJobDto> => {
    const res = await axiosInstance.get<{ data: BulkUploadJobDto }>(`/leads/bulk-upload/status/${jobId}`)
    return res.data.data
  },

  getBulkUploadLogs: async (jobId: string, page: number = 1, pageSize: number = 50): Promise<PaginatedBulkUploadJobLogs> => {
    const res = await axiosInstance.get<PaginatedBulkUploadJobLogs>(`/leads/bulk-upload/status/${jobId}/logs`, {
      params: { page, pageSize }
    })
    return res.data
  },

  cancelBulkUpload: async (jobId: string): Promise<{ success: boolean; message: string }> => {
    const res = await axiosInstance.post<{ success: boolean; message: string }>(`/leads/bulk-upload/${jobId}/cancel`)
    return res.data
  },

  // ── Activities ──────────────────────────────────────────────────────────────

  getActivities: async (id: string): Promise<LeadActivity[]> => {
    const res = await axiosInstance.get<{ data: LeadActivity[] }>(`/leads/${id}/activities`)
    return res.data.data
  },

  addActivity: async (id: string, payload: AddActivityPayload): Promise<LeadActivity> => {
    const res = await axiosInstance.post<{ data: LeadActivity }>(`/leads/${id}/activities`, payload)
    return res.data.data
  },

  // ── Status history ──────────────────────────────────────────────────────────

  getStatusHistory: async (id: string): Promise<StatusHistoryEntry[]> => {
    const res = await axiosInstance.get<{ data: StatusHistoryEntry[] }>(`/leads/${id}/status-history`)
    return res.data.data
  },

  // ── Tags on lead ────────────────────────────────────────────────────────────

  addTag: async (id: string, tagId: string): Promise<Lead> => {
    const res = await axiosInstance.post<{ data: Lead }>(`/leads/${id}/tags/${tagId}`)
    return res.data.data
  },

  removeTag: async (id: string, tagId: string): Promise<Lead> => {
    const res = await axiosInstance.delete<{ data: Lead }>(`/leads/${id}/tags/${tagId}`)
    return res.data.data
  },

  search: async (query: string): Promise<Lead[]> => {
    const res = await axiosInstance.get<{ data: PaginatedLeads }>('/leads', {
      params: { search: query, page: 1, pageSize: 20 },
    })
    return res.data.data.items
  },

  updateHelpAssignee: async (id: string, helpAssigneeId: string | null): Promise<Lead> => {
    const res = await axiosInstance.patch<{ data: Lead }>(`/leads/${id}/help-assignee`, { helpAssigneeId })
    return res.data.data
  },
}

// ── Tags API ──────────────────────────────────────────────────────────────────

export interface TagCreateResult {
  tagId?: string | null
  isBulk: boolean
  createdOrgs: string[]
  skippedOrgs: string[]
}

export const tagsApi = {

  getAll: async (filters?: { orgId?: string | null }): Promise<Tag[]> => {
    const res = await axiosInstance.get<{ data: Tag[] }>('/tags', { params: filters })
    return res.data.data
  },

  create: async (payload: CreateTagPayload): Promise<TagCreateResult> => {
    const res = await axiosInstance.post<{ data: TagCreateResult }>('/tags', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateTagPayload): Promise<Tag> => {
    const res = await axiosInstance.put<{ data: Tag }>(`/tags/${id}`, payload)
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/tags/${id}`)
  },
}
